import React, { useEffect, useRef, useState } from 'react';

interface CountUpProps {
  /** Target value to count up to. */
  value: number;
  /** Duration in ms (default 1100, per the design spec). */
  duration?: number;
  /** Text before the number (e.g. "₹"). */
  prefix?: string;
  /** Text after the number (e.g. "%", "+"). */
  suffix?: string;
  /** Decimal places to render. */
  decimals?: number;
  /** Locale for grouping (default en-IN). */
  locale?: string;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Counts up from 0 to `value` on mount with an ease-out curve. Renders the
 * final value immediately when the user prefers reduced motion.
 */
const CountUp: React.FC<CountUpProps> = ({
  value,
  duration = 1100,
  prefix = '',
  suffix = '',
  decimals = 0,
  locale = 'en-IN',
}) => {
  const [display, setDisplay] = useState(() => (prefersReducedMotion() ? value : 0));
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (prefersReducedMotion() || !Number.isFinite(value)) {
      setDisplay(value);
      return;
    }
    startRef.current = null;
    const step = (ts: number) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic ~ power2/3.out
      setDisplay(value * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else setDisplay(value);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration]);

  const rounded =
    decimals > 0
      ? Number(display.toFixed(decimals))
      : Math.round(display);
  const formatted = rounded.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <>
      {prefix}
      {formatted}
      {suffix}
    </>
  );
};

export default CountUp;
